import { useEffect, useRef } from 'react';
import { useUser } from '@clerk/react';

import { isClerkAuthEnabled } from '@/utils/auth/authMode';

/**
 * Backend may PATCH Clerk first/last names after invite reconcile / registration link.
 * The Clerk JS client cache stays stale until reload — without this, UserProfile shows
 * empty name fields on the first open.
 */
export function useReloadClerkUserAfterServerProfileSync(ready: boolean): void {
  const { user, isLoaded } = useUser();
  const didReloadRef = useRef(false);

  useEffect(() => {
    if (!isClerkAuthEnabled() || !ready || !isLoaded || !user || didReloadRef.current) {
      return;
    }

    const needsRefresh = !user.firstName?.trim() && !user.lastName?.trim();
    if (!needsRefresh) {
      didReloadRef.current = true;
      return;
    }

    // Give tenant provision middleware a moment to PATCH Clerk before we reload the client cache.
    const timeoutId = window.setTimeout(() => {
      didReloadRef.current = true;
      void user.reload().catch((error: unknown) => {
        console.warn('[ChurnSignal] Failed to reload Clerk user after profile sync:', error);
        didReloadRef.current = false;
      });
    }, 750);

    return () => window.clearTimeout(timeoutId);
  }, [isLoaded, ready, user]);
}
